import { useMemo } from 'react';
import type { PublicView } from '@ringmaster/shared';
import { BroadcastFrame } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { StageHUD, LowerThird } from './components/index.js';
import { ornaments } from './ornaments.js';
import { LobbyScreen } from './LobbyScreen.js';
import { DealScreenContent } from './DealScreen.js';
import { NightScreenContent } from './NightScreen.js';
import { DawnScreenContent } from './DawnScreen.js';
import { DayScreenContent } from './DayScreen.js';
import { VoteScreenContent } from './VoteScreen.js';
import { EndScreenContent } from './EndScreen.js';
import { WhisperStage } from './WhisperStage.js';

/**
 * StageView — the persistent broadcast SHELL for the narrator-only host TV surface
 * (and the /stage spectator route). Driven entirely by the PUBLIC view (no secrets
 * reach a Stage), it composites, from back to front:
 *
 *   SceneStack (parallax set, by phase + winner)
 *     → BroadcastFrame (1920×1080 scale-to-fit)
 *       → ProsceniumFrame (curtains / valance / filigree / grain / vignette,
 *                          lowerThird = LowerThird narration plate)
 *         → StageHUD (top: phase + round + timer)  [when the phase carries a timer]
 *         → phase content (the *Content inner screen for the current phase)
 *
 * The chrome (SceneStack, frame, proscenium, HUD, narration plate) is PERSISTENT —
 * it doesn't unmount between phases, so only the inner phase content crossfades and
 * the moon/curtains/grain stay put. Each phase screen renders its OWN player layout
 * (arc / line / rail) and its OWN reveal choreography, so the shell does not render
 * a global PlayerRail (that would double the busts on Vote/Dawn/Day).
 *
 * Pre-game Title is handled by the Host route. LOBBY is a special case: the
 * broadcast lobby (LobbyScreen) is already a COMPLETE self-framed composition —
 * it renders its OWN SceneStack LOBBY backdrop, marquee, room-code plaque, QR
 * sign and "Tonight's Cast" playbill — so StageView renders it directly INSTEAD
 * of the proscenium shell (wrapping it in the shell would double the backdrop and
 * frame). Every non-Host StageView surface (/stage spectator, the Discord stage
 * pane) passes isHost=false, so LobbyScreen renders READ-ONLY: no Start, no kick,
 * no config, no narrator toggle, no mute — a pure marquee. StageView covers
 * LOBBY → END; the Host route keeps rendering the SAME LobbyScreen pre-game (with
 * its host handlers), so that surface is unchanged.
 *
 * `isHost` gates gameplay controls (End discussion / Play again). Those controls
 * are host chrome the Host route renders around StageView (via RingmasterBar), not
 * inside this broadcast surface — so onEndDiscussion / onPlayAgain are accepted for
 * signature stability but the reveal screens themselves render no controls.
 */
export interface StageViewProps {
  publicView: PublicView;
  caption: string | null;
  /** True while the narrator is actively speaking (drives the LowerThird plate). */
  isSpeaking?: boolean;
  onEndDiscussion: () => void;
  onPlayAgain: () => void;
  /**
   * Whether this surface belongs to the host (controls the game). Defaults to true.
   * A pure spectator (/stage broadcast route) passes false — no gameplay control
   * ever renders (already the case: controls live in the Host route's RingmasterBar).
   */
  isHost?: boolean;
}

// Phases whose screen owns its own headline (Deal title, Dawn plaque, End banner,
// the DISCUSS + VOTE hero countdowns) and shouldn't get a competing StageHUD
// timer/label on top. DayScreen (DISCUSS) and VoteScreen (VOTE) each render their
// OWN big gold countdown as the phase hero, so the shell HUD is suppressed to avoid
// a double timer. RESOLVE_VOTE is intentionally NOT here — its reveal sequence owns
// the frame and it keeps the shell HUD (unchanged).
const NO_HUD_PHASES = new Set<PublicView['phase']>(['DEAL', 'DAWN', 'DISCUSS', 'VOTE', 'END']);

export function StageView({ publicView, caption, isSpeaking = false }: StageViewProps) {
  const phase = publicView.phase;
  const manifest = scenesForGame('bloodmoon');
  const winner = phase === 'END' ? publicView.result?.winner : undefined;

  // LOBBY is a complete self-framed broadcast composition (its own SceneStack +
  // marquee): render it directly, filling the positioned parent, rather than
  // through the proscenium shell (which would double the backdrop/frame). Always
  // READ-ONLY here — the pre-game host lobby with controls is owned by the Host
  // route, which mounts LobbyScreen itself. No handlers → no host controls show.
  // The lobby is game-agnostic (roster + room code), so BOTH games share it.
  if (phase === 'LOBBY') {
    return (
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--ink-0, #060406)' }}>
        <LobbyScreen view={publicView} isHost={false} />
      </div>
    );
  }

  // ── The Whisper — its own broadcast surface (candle + ring + runes + score).
  //    (undefined gameId → 'bloodmoon', so Blood Moon is unaffected.) The Whisper
  //    stage owns its own proscenium shell + arcana SceneStack for every non-LOBBY
  //    phase, including END (the final leaderboard).
  if (publicView.gameId === 'whisper') {
    return <WhisperStage publicView={publicView} caption={caption} isSpeaking={isSpeaking} />;
  }

  const content = useMemo(() => renderPhase(publicView), [publicView]);

  // The HUD carries the live countdown; skip it on the self-titled reveal phases
  // (DEAL has its own title, END has its winner banner) to avoid a competing header.
  const showHud = !NO_HUD_PHASES.has(phase);

  // The proscenium ornaments + HUD + phase content, sitting ABOVE the scene inside
  // the 1920×1080 design space (SceneStack renders its children at z-index 10).
  const ornamentsAndContent = (
    <ProsceniumFrame
      cornerSrc={ornaments.filigreeCorner}
      grainSrc={ornaments.grainTile}
      lowerThird={<LowerThird caption={caption} isSpeaking={isSpeaking} />}
    >
      {showHud && (
        // Sit the HUD near the top of the frame (the valance is gone). The
        // filigree corners hug the left/right edges below SAFE=64, so this
        // centered timer/label clears them. StageHUD positions from its own
        // container's top.
        <div style={{ position: 'absolute', top: '4%', left: 0, right: 0, height: 0, zIndex: 6 }}>
          <StageHUD
            phase={phase}
            round={publicView.round}
            gameId={publicView.gameId}
            timerEndsAt={publicView.timerEndsAt}
            paused={publicView.paused}
            timerRemainingMs={publicView.timerRemainingMs}
          />
        </div>
      )}
      {content}
    </ProsceniumFrame>
  );

  // SceneStack lives INSIDE BroadcastFrame's 1920×1080 design space (scene art is
  // authored at that resolution and must scale WITH the frame). It is the deepest
  // layer; the ornaments + content render above it. BroadcastFrame's interior is
  // TRANSPARENT so the scene shows through — the letterbox bars come from the outer
  // `fill` div's --ink-0. (The old order — SceneStack wrapping BroadcastFrame —
  // meant BroadcastFrame's own opaque --ink-0 fill painted OVER the scene, hiding it
  // entirely behind a hard-edged rectangle. Matches the *ScreenDemo composition.)
  const framed = (
    <BroadcastFrame background="transparent">
      {manifest ? (
        <SceneStack manifest={manifest} phase={phase} winner={winner}>
          {ornamentsAndContent}
        </SceneStack>
      ) : (
        ornamentsAndContent
      )}
    </BroadcastFrame>
  );

  // Fill the positioned parent exactly (Host route wrapper + /stage route + the
  // CardLab preview box are all position:relative). absolute-inset gives a
  // DEFINITE box so BroadcastFrame's Resize observer measures a real height —
  // `minHeight:100dvh` on a plain block does NOT (the scale would collapse). This
  // div's --ink-0 is also the letterbox color outside the 16:9 design box.
  const fill: React.CSSProperties = { position: 'absolute', inset: 0, background: 'var(--ink-0, #060406)' };

  return <div style={fill}>{framed}</div>;
}

/** Map the current phase to its inner *Content screen (the shell frames them). */
function renderPhase(view: PublicView) {
  switch (view.phase) {
    case 'DEAL':
      return <DealScreenContent />;
    case 'NIGHT_INTRO':
    case 'NIGHT_WOLVES':
    case 'NIGHT_INTERLUDE':
    case 'NIGHT_SEER':
    case 'RESOLVE_NIGHT':
      return <NightScreenContent view={view} isHost={false} />;
    case 'DAWN':
      return <DawnScreenContent view={view} isHost={false} />;
    case 'DISCUSS':
      return <DayScreenContent view={view} isHost={false} />;
    case 'VOTE':
    case 'RESOLVE_VOTE':
      return <VoteScreenContent view={view} isHost={false} />;
    case 'END':
      return <EndScreenContent view={view} isHost={false} />;
    case 'LOBBY':
    default:
      // LOBBY is handled by the early return in StageView (self-framed LobbyScreen),
      // so this branch is unreachable; kept for switch exhaustiveness.
      return null;
  }
}
